// Rut planı harita bileşeni — react-leaflet + OpenStreetMap tile'ları.
// Müşteri noktalarını güne göre renkli gösterir; numbered=true ise sıralı
// rota önizlemesi için numaralı pin (DivIcon) çizer ve noktaları çizgiyle bağlar.
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Tooltip, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

// İstanbul merkezli varsayılan görünüm (geo'su olmayan plan için).
const DEFAULT_CENTER = [41.015, 28.979];
const DEFAULT_ZOOM = 11;

function numberedIcon(order, color) {
  return L.divIcon({
    className: 'route-pin',
    html: `<div style="
      width:26px;height:26px;border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      background:${color};border:2px solid #fff;
      box-shadow:0 2px 6px rgba(15,23,42,0.35);
      display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);color:#fff;font-weight:800;font-size:12px;">${order}</span>
    </div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });
}

// Satış/yoğunluk ısı katmanı — leaflet.heat. Her nokta [lat, lng, intensity(0..1)].
function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return undefined;
    const layer = L.heatLayer(
      points.map((p) => [p.lat, p.lng, Number.isFinite(p.intensity) ? p.intensity : 1]),
      {
        radius: 28,
        blur: 22,
        maxZoom: 16,
        max: 1,
        minOpacity: 0.28,
        gradient: { 0.2: '#0073ea', 0.4: '#00c875', 0.6: '#fdab3d', 0.8: '#e2445c', 1.0: '#b91c1c' },
      },
    );
    layer.addTo(map);
    return () => { map.removeLayer(layer); };
  }, [points, map]);
  return null;
}

// #712 Alan (dikdörtgen) seçimi. Aktifken harita sürüklemesi/box-zoom kapatılır; kullanıcı
// fareyle bir dikdörtgen çizer, bıraktığında dikdörtgen içindeki noktaların id'leri onSelect ile
// döner. Harici plugin gerektirmez — Leaflet'in kendi olayları + L.rectangle ile.
function AreaSelect({ active, points, onSelect }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return undefined;
    const container = map.getContainer();
    map.dragging.disable();
    if (map.boxZoom) map.boxZoom.disable();
    container.style.cursor = 'crosshair';
    let start = null;
    let rect = null;
    const clearRect = () => { if (rect) { map.removeLayer(rect); rect = null; } };
    const onDown = (e) => {
      start = e.latlng;
      rect = L.rectangle([start, start], { color: '#7a3cff', weight: 2, dashArray: '5 5', fillColor: '#7a3cff', fillOpacity: 0.10 }).addTo(map);
    };
    const onMove = (e) => { if (start && rect) rect.setBounds(L.latLngBounds(start, e.latlng)); };
    const onUp = (e) => {
      if (!start) return;
      const bounds = L.latLngBounds(start, e.latlng);
      const ids = (points || [])
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && p.id != null && bounds.contains([p.lat, p.lng]))
        .map((p) => p.id);
      clearRect();
      start = null;
      if (ids.length) onSelect?.(ids);
    };
    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    return () => {
      map.off('mousedown', onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      clearRect();
      map.dragging.enable();
      if (map.boxZoom) map.boxZoom.enable();
      container.style.cursor = '';
    };
  }, [active, points, onSelect, map]);
  return null;
}

// points değişince haritayı noktaları kapsayacak şekilde yakınlaştırır.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

export default function RouteMap({ points = [], numbered = false, height = 360, showLine = false, heatmap = false, geometry = null, onPointRemove = null, selectable = false, onAreaSelect = null, selectedIds = null }) {
  // #712 Alan seçim modu (yalnız selectable iken). Aktifken fareyle dikdörtgen çizilir.
  const [selMode, setSelMode] = useState(false);
  const valid = useMemo(
    () => points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)),
    [points],
  );

  // Sunucudan gelen rota geometrisi (GeoJSON LineString) → [lat,lng] dizisi.
  const geoLine = useMemo(() => {
    if (!geometry) return null;
    try {
      const g = typeof geometry === 'string' ? JSON.parse(geometry) : geometry;
      const coords = g?.coordinates || g?.geometry?.coordinates;
      if (!Array.isArray(coords)) return null;
      // GeoJSON [lng,lat] → Leaflet [lat,lng]
      return coords.filter((c) => Array.isArray(c) && c.length >= 2).map((c) => [c[1], c[0]]);
    } catch {
      return null;
    }
  }, [geometry]);

  return (
    // height='100%' geldiğinde (tam ekran) sarmalayıcı da yüksekliği üst kaptan almalı,
    // yoksa MapContainer'ın %100'ü 0'a çöker ve harita boş görünür (#152 madde 6).
    <div style={{ position: 'relative', height: height === '100%' ? '100%' : undefined, borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border-color, #e5e7eb)' }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height, width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={valid} />
        {selectable && <AreaSelect active={selMode} points={valid} onSelect={onAreaSelect} />}

        {heatmap && <HeatLayer points={valid} />}

        {/* Sunucu rota geometrisi varsa onu çiz (gerçek yol), yoksa düz çizgi. */}
        {!heatmap && geoLine && geoLine.length > 1 && (
          <Polyline
            positions={geoLine}
            pathOptions={{ color: '#0073ea', weight: 4, opacity: 0.8 }}
          />
        )}
        {!heatmap && !geoLine && showLine && valid.length > 1 && (
          <Polyline
            positions={valid.map((p) => [p.lat, p.lng])}
            pathOptions={{ color: '#7a3cff', weight: 3, opacity: 0.55, dashArray: '6 8' }}
          />
        )}

        {!heatmap && valid.map((p, i) =>
          numbered ? (
            <Marker key={p.key ?? i} position={[p.lat, p.lng]} icon={numberedIcon(p.order ?? i + 1, p.color || '#0073ea')}>
              <Tooltip direction="top" offset={[0, -24]}>
                <strong>{p.order ?? i + 1}.</strong> {p.label}
              </Tooltip>
              {onPointRemove && (
                <Popup>
                  <div style={{ textAlign: 'center', minWidth: 140 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{p.order ?? i + 1}. {p.label}</div>
                    <button
                      type="button"
                      onClick={() => onPointRemove(p)}
                      style={{ border: '1px solid #e2445c', background: '#fef2f2', color: '#e2445c', fontWeight: 700, fontSize: 12, borderRadius: 8, padding: '5px 12px', cursor: 'pointer' }}
                    >
                      Rotadan çıkar
                    </button>
                  </div>
                </Popup>
              )}
            </Marker>
          ) : (
            <CircleMarker
              key={p.key ?? i}
              center={[p.lat, p.lng]}
              radius={selectedIds?.has(p.id) ? 8 : 7}
              pathOptions={{ color: '#fff', weight: 2, fillColor: selectedIds?.has(p.id) ? '#7a3cff' : (p.color || '#0073ea'), fillOpacity: 0.95 }}
            >
              <Tooltip direction="top">
                {p.code ? <span style={{ color: '#64748b' }}>{p.code} · </span> : null}
                {p.label}
              </Tooltip>
            </CircleMarker>
          ),
        )}
      </MapContainer>

      {/* #712 Alan seçim aracı — haritanın sağ üstünde. Aktifken fareyle bir dikdörtgen
          çizilir; içindeki müşteriler seçilir. */}
      {selectable && (
        <>
          <button
            type="button"
            onClick={() => setSelMode((v) => !v)}
            title={selMode ? 'Alan seçimini kapat' : 'Alan seçerek çoklu müşteri seç'}
            style={{
              position: 'absolute', top: 10, right: 10, zIndex: 600,
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 11px',
              borderRadius: 10, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1px solid ${selMode ? '#7a3cff' : 'var(--border-color,#d1d5db)'}`,
              background: selMode ? '#7a3cff' : 'var(--bg-card,#fff)',
              color: selMode ? '#fff' : 'var(--text-primary,#1f2937)',
              boxShadow: '0 2px 8px rgba(15,23,42,0.18)',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2.5" />
            </svg>
            Alan Seç
          </button>
          {selMode && (
            <div style={{
              position: 'absolute', top: 10, left: 10, zIndex: 600, maxWidth: '70%',
              padding: '6px 10px', borderRadius: 8, fontSize: 12,
              background: 'rgba(122,60,255,0.92)', color: '#fff', boxShadow: '0 2px 8px rgba(15,23,42,0.2)',
            }}>
              Fareyle bir alan çizin — içindeki müşteriler seçilir.
            </div>
          )}
        </>
      )}

      {valid.length === 0 && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.72)', color: 'var(--text-secondary, #6b7280)', fontSize: 13,
          pointerEvents: 'none', zIndex: 500,
        }}>
          Konum bilgisi olan müşteri yok.
        </div>
      )}
    </div>
  );
}

// Harita altı gün-renk açıklaması (legend).
export function DayLegend({ days = [], style }) {
  if (!days.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, padding: '8px 4px 0', ...style }}>
      {days.map((d) => (
        <span key={d.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary, #475569)' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, display: 'inline-block' }} />
          {d.label}
        </span>
      ))}
    </div>
  );
}
